import CommonDialog from '@/components/common/dialog/CommonDialog.vue'
import { useMemberManagement } from './useMemberManagement'
import { ref, useTemplateRef, computed } from 'vue'
import { storeToRefs } from 'pinia'
import type { SetAccountRequestParam } from '@/types/api/dupAuth/setAccountRequestParam'
import MiiaFwConfig from '@/configs/config'
import { useUserManagementStore } from '@/stores/userManagementStore'
import { useMiiaFwStore } from '@/stores/miiaFwStore'

export const useMemberRegistrationModal = () => {
  const commonDialog = useTemplateRef<InstanceType<typeof CommonDialog>>('commonDialog')
  const { itemsTyped } = useMemberManagement()
  const userManagementStore = useUserManagementStore()
  const miiaFwStore = useMiiaFwStore()
  const { organizationCode } = storeToRefs(miiaFwStore)
  const selectedValue = ref<'newCreate' | 'existAdd'>('newCreate')
  const errors = ref<string[]>([])

  /**
   * 団体リストをcomputedで作成
   * itemsTypedから団体名をユニークに抽出し、選択肢の形式({ value, text })に整形する。
   */
  const organizationList = computed(() => {
    if (!itemsTyped.value) return []
    const set = new Set(itemsTyped.value.map(u => u.organizationName).filter(Boolean))
    return Array.from(set).map(name => ({ value: name, text: name }))
  })

  /**
   * モーダルを開く関数。
   * バリデーションエラーをクリアし、CommonDialogのopenDialog()を呼んでモーダルを表示。
   * Promiseで開閉完了を通知し、選択状態を初期化する。
   * @returns {Promise<boolean>} モーダルのOKで完了すればtrue
   */
  const openModal = async (): Promise<boolean> => {
    reset()
    if (!commonDialog.value) {
      return false
    }
    return await commonDialog.value
      ?.openDialog()
      .then(() => true)
      .catch(() => false)
  }

  /**
   * 新規ユーザーを登録する非同期関数。
   * APIリクエストパラメータを作成し、ユーザ管理ストアのfetchSetAccountを呼ぶ。
   * @param organizationName - 団体名（必須）
   * @param phoneNumber - 電話番号（必須）
   * @param userName - ユーザー名（任意）
   */
  const setAccount = async (phoneNumber: string, userName: string) => {
    // 既存のパラメータをセット
    const params: SetAccountRequestParam = {
      system: MiiaFwConfig.config.sysId,
      phoneNumber: phoneNumber as string,
      organizationCode: organizationCode.value as string
    }
    if (userName !== '') {
      params.additionalInfo = { name: userName }
    }
    await userManagementStore.fetchSetAccount(params)
  }

  /**
   * errorsとselectedValueを初期化する関数
   */
  function reset() {
    errors.value = []
    selectedValue.value = 'newCreate'
  }

  return {
    selectedValue,
    organizationList,
    openModal,
    errors,
    setAccount,
    commonDialog
  }
}
